import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { StatCard } from '../ui/StatCard';
import { AlertRow } from '../shell/AlertRow';
import { StatusBanner } from '../shell/StatusBanner';
import { DamageTierSelector } from './DamageTierSelector';
import { THIEVERY_OPERATIONS } from '../../constants/thievery';
import { formatNumber } from '../../utils/format';
import { COLORS, INPUT_STYLE } from '../../constants/theme';
import { isProtected } from '../../utils/protection';
import { ThieveryResult as ThieveryResultComponent } from '../ThieveryResult';
import type { ThieveryShellProps } from '../../pages/Thievery';
import type { ThieveryOperationType } from '../../types/thievery';

const selectStyle = { ...INPUT_STYLE, border: `1px solid ${COLORS.inputBorder}` } as const;

/**
 * SF-149: thin wrapper around the existing (already desktop-appropriate) Thievery JSX — no layout
 * redesign. Adds a real, focus-managed `<h1>`, replaces the ad hoc inline "no thieves" `<div>`
 * with `AlertRow`, replaces the ad hoc target-protection caption `<div>` with
 * `StatusBanner tone="protection"`, and replaces the standalone "not enough thieves" warning line
 * with the shared `submitDisabledReason` caption — everything else (the StatCard row including
 * its Est. Success card, "Change Kingdom" box, combined Target/Operation/Submit box, and the
 * trailing `ThieveryResultComponent` in its existing below-the-box position) is unchanged from
 * pre-SF-149 `Thievery.tsx`.
 */
export function DesktopThieveryShell({
  province,
  thiefData,
  tpaStrength,
  hasNoThieves,
  kingdomNumber,
  setKingdomNumber,
  islandNumber,
  setIslandNumber,
  handleChangeKingdom,
  isLoadingKingdom,
  kingdomMembers,
  selectedTarget,
  handleSelectTarget,
  selectedTargetProtectionCountdown,
  selectedOperation,
  setSelectedOperation,
  operation,
  isIntel,
  damageTier,
  setDamageTier,
  allowedTiers,
  tierLabels,
  thievesSentPreview,
  operationInfo,
  submitDisabledReason,
  isSubmitting,
  submitError,
  handleConductOperation,
  operationResult,
}: ThieveryShellProps) {
  const location = useLocation();
  const h1Ref = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    h1Ref.current?.focus();
  }, [location.pathname]);

  return (
    <div className="space-y-4">
      <h1 ref={h1Ref} tabIndex={-1} className="text-xl font-bold text-game-50 outline-none">
        Thievery
      </h1>

      {hasNoThieves && (
        <AlertRow
          severity="warning"
          message="You have no thieves. Train thieves in the Military page to conduct operations."
        />
      )}

      {/* Stat Cards */}
      <div className="flex gap-3">
        <div className="w-36">
          <StatCard
            label="Thieves"
            value={formatNumber(thiefData.thieves)}
            sub={`${thiefData.tpa.toFixed(2)} per acre`}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="TPA"
            value={thiefData.tpa.toFixed(2)}
            sub={tpaStrength.label}
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Stealth"
            value={`${thiefData.stealth.toFixed(0)}%`}
            sub="Current stealth"
            borderless
          />
        </div>
        <div className="w-36">
          <StatCard
            label="Est. Success"
            value={operationInfo ? `~${operationInfo.successRate}%` : '—'}
            sub={operationInfo ? operationInfo.operation.name : 'Select operation'}
            borderless
          />
        </div>
      </div>

      {/* Change Kingdom */}
      <div
        className="rounded p-4"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>Kingdom</span>
          <input
            type="number"
            placeholder="14"
            value={kingdomNumber}
            onChange={(e) => setKingdomNumber(e.target.value)}
            onFocus={(e) => e.target.select()}
            className="w-16 rounded text-sm py-1.5 px-2"
            style={selectStyle}
            disabled={hasNoThieves}
          />
          <span className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>Island</span>
          <input
            type="number"
            placeholder="40"
            value={islandNumber}
            onChange={(e) => setIslandNumber(e.target.value)}
            onFocus={(e) => e.target.select()}
            className="w-16 rounded text-sm py-1.5 px-2"
            style={selectStyle}
            disabled={hasNoThieves}
          />
          <Button onClick={handleChangeKingdom} disabled={isLoadingKingdom || hasNoThieves} variant="secondary">
            {isLoadingKingdom ? 'Loading...' : 'Change Kingdom'}
          </Button>
        </div>
      </div>

      {/* Target + Operation + Submit */}
      <div
        className="rounded overflow-hidden"
        style={{ backgroundColor: COLORS.card, border: `1px solid ${COLORS.border}` }}
      >
      {/* Target Selection */}
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-3">
          <span className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>Target</span>
          <select
            value={selectedTarget}
            onChange={(e) => handleSelectTarget(e.target.value)}
            className="flex-1 rounded text-sm py-1.5 px-2"
            style={selectStyle}
            disabled={hasNoThieves}
            aria-describedby={selectedTargetProtectionCountdown ? 'target-protection-status' : undefined}
          >
            <option value="">Select a target...</option>
            {kingdomMembers.map((member) => {
              const memberProtected = isProtected(member.protectionExpiresAtTick, province?.sync.currentTick ?? 0);
              return (
                <option key={member.provinceId} value={member.provinceId} disabled={memberProtected}>
                  {member.provinceName} ({member.location}){memberProtected ? ' (Protected)' : ''}
                </option>
              );
            })}
          </select>
        </div>
        {selectedTargetProtectionCountdown && (
          <div id="target-protection-status">
            <StatusBanner
              tone="protection"
              icon="🛡️"
              headline="Target is protected"
              detail={selectedTargetProtectionCountdown}
              variant="full"
            />
          </div>
        )}
      </div>

      {/* Operation Selection */}
      <div className="p-4 space-y-3" style={{ borderTop: `1px solid ${COLORS.border}` }}>
        <div className="flex items-center gap-3">
          <span className="text-sm whitespace-nowrap" style={{ color: COLORS.text.secondary }}>Operation</span>
          <select
            value={selectedOperation}
            onChange={(e) => {
              setSelectedOperation(e.target.value as ThieveryOperationType);
            }}
            className="flex-1 rounded text-sm py-1.5 px-2"
            style={selectStyle}
            disabled={!selectedTarget || hasNoThieves}
          >
            <option value="">Select an operation...</option>
            <optgroup label="Intelligence">
              {THIEVERY_OPERATIONS.filter((op) => op.category === 'INTEL').map((op) => (
                <option key={op.type} value={op.type}>{op.name}</option>
              ))}
            </optgroup>
            <optgroup label="Resource Theft">
              {THIEVERY_OPERATIONS.filter((op) => op.category === 'RESOURCE').map((op) => (
                <option key={op.type} value={op.type}>{op.name}</option>
              ))}
            </optgroup>
            <optgroup label="Destructive">
              {THIEVERY_OPERATIONS.filter((op) => op.category === 'DESTRUCTIVE').map((op) => (
                <option key={op.type} value={op.type}>{op.name}</option>
              ))}
            </optgroup>
            <optgroup label="Duration Effects">
              {THIEVERY_OPERATIONS.filter((op) => op.category === 'DURATION').map((op) => (
                <option key={op.type} value={op.type}>{op.name}</option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Tier selector (non-intel) or auto-commit info (intel) */}
        {operation && (
          isIntel ? (
            <div className="text-sm" style={{ color: COLORS.text.secondary }}>
              Auto-commit: {formatNumber(thievesSentPreview)} thieves (
              {(operation.thiefCostPercent * 100).toFixed(0)}% of {formatNumber(thiefData.thieves)})
            </div>
          ) : (
            <div>
              <div id="damage-tier-label" className="text-sm mb-1" style={{ color: COLORS.text.secondary }}>
                {operation.category === 'RESOURCE' ? 'Operation Scale'
                  : operation.category === 'DURATION' ? 'Operation Intensity'
                  : 'Damage Tier'}
              </div>
              <DamageTierSelector
                value={damageTier}
                onChange={setDamageTier}
                allowedTiers={allowedTiers}
                labels={tierLabels}
              />
            </div>
          )
        )}

        {/* Description */}
        {operationInfo && (
          <p className="text-sm" style={{ color: COLORS.text.tertiary }}>
            {operationInfo.operation.description}
          </p>
        )}
      </div>

      {/* Submit Bar */}
      <div
        className="px-4 py-3 space-y-3"
        style={{ borderTop: `1px solid ${COLORS.border}` }}
      >
        {submitError && (
          <div className="text-sm" style={{ color: COLORS.negative }}>{submitError}</div>
        )}

        <div className="flex items-center gap-6 text-sm flex-wrap">
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Sending: </span>
            <span className="font-mono" style={{ color: thievesSentPreview > 0 ? COLORS.text.primary : COLORS.text.tertiary }}>
              {formatNumber(thievesSentPreview)} thieves
            </span>
          </span>
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Stealth Cost: </span>
            <span className="font-mono" style={{ color: operationInfo ? COLORS.text.primary : COLORS.text.tertiary }}>
              {operationInfo ? `~${Math.round(operationInfo.stealthCost)}%` : '—'}
            </span>
          </span>
          <span className="whitespace-nowrap">
            <span style={{ color: COLORS.text.tertiary }}>Success: </span>
            <span className="font-mono" style={{ color: operationInfo ? COLORS.text.primary : COLORS.text.tertiary }}>
              {operationInfo ? `~${operationInfo.successRate}%` : '—'}
            </span>
          </span>
          {operationInfo && operationInfo.estLoss > 0 && (
            <span className="whitespace-nowrap">
              <span style={{ color: COLORS.text.tertiary }}>Est. Loss on Failure: </span>
              <span className="font-mono" style={{ color: COLORS.text.primary }}>
                ~{formatNumber(operationInfo.estLoss)} thieves
              </span>
              <span className="text-xs ml-1" style={{ color: COLORS.text.tertiary }}>
                (assumes target TPA ≈ 1.0)
              </span>
            </span>
          )}
        </div>

        {submitDisabledReason && (
          <div className="text-xs" style={{ color: COLORS.warning }}>
            {submitDisabledReason}
          </div>
        )}

        <div className="flex items-center">
          <div className="flex gap-2 ml-auto">
            <Button
              onClick={handleConductOperation}
              isLoading={isSubmitting}
              disabled={!!submitDisabledReason}
            >
              Conduct Operation
            </Button>
          </div>
        </div>
      </div>
      </div>

      {/* Operation Result */}
      {operationResult && <ThieveryResultComponent result={operationResult} />}
    </div>
  );
}
